import { ChangeDetectionStrategy, Component, inject, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { EditorRole, getEditorRoleViewValue } from '../editor.role.enum';
import { generateSecurePassword } from '../../core/util';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { EditorJsonModel } from '../editor.json.model';
import { MatDividerModule } from '@angular/material/divider';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { AsyncPipe } from '@angular/common';
import { select, Store } from '@ngxs/store';
import { SubjectState } from '../../subject/ngxs/subject/subject.state';
import { SubjectActions } from '../../subject/ngxs/subject/subject.actions';
import { AppSelectors } from '../../app.state';
import { PasswordStrengthComponent } from '../password-strength/password-strength.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'pixl-editor-edit',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    MatDividerModule,
    AsyncPipe,
    PasswordStrengthComponent,
    TranslocoModule,
  ],
  templateUrl: './editor-edit.component.html',
  styleUrl: './editor-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class EditorEditComponent implements OnInit, AfterViewInit {
  private translocoService = inject(TranslocoService);
  private fb = inject(FormBuilder);
  private store = inject(Store);

  dialogRef = inject(MatDialogRef<EditorEditComponent, EditorJsonModel>);
  data = inject<EditorJsonModel>(MAT_DIALOG_DATA);
  initialRole = this.data.role;
  strongPassword = false;

  subjects$ = this.store.select(SubjectState.getSubjects);
  public authState = select(AppSelectors.authState);

  public dataSubjects: SubjectJsonModel[] = [];

  roles: EditorRole[] = Object.values(EditorRole) as EditorRole[];
  showPassword = false;

  isEditMode = !!this.data?.email;

  userForm = this.fb.group({
    email: [this.data.email, [Validators.required, Validators.email]],
    password: [this.data?.password || '', this.isEditMode ? [] : [Validators.required]],
    role: [this.data.role, [Validators.required]],
    subjects: [this.data.subjects || []],
  });

  ngOnInit(): void {
    this.store.dispatch(new SubjectActions.FetchAll());
  }

  ngAfterViewInit(): void {
    this.subjects$.subscribe({
      next: (data) => {
        this.dataSubjects = data;
      }
    });
  }

  onSave(): void {
    // these ifs make sure that even if you enable the buttons in the dialog you cannot save the changes if your role changed
    if (this.authState().editor?.email === this.userForm.value.email) {
      if (this.initialRole === this.userForm.value.role && this.userForm.valid) {
        this.data.update(this.userForm.value);
        this.dialogRef.close(this.data);
      }
    }
    if (this.userForm.valid) {
      this.data.update(this.userForm.value);
      this.dialogRef.close(this.data);
    }
  }

  onPasswordStrengthChanged(event: boolean) {
    this.strongPassword = event;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  generatePassword(): void {
    this.userForm.patchValue({
      password: generateSecurePassword()
    });
  }

  public getEditorRole(role: EditorRole): string {
    return this.translocoService.translate(role);
  }

  protected readonly getEditorRoleViewValue = getEditorRoleViewValue;
}
